import * as React from "react"
import { format } from "date-fns"
import { tr } from "date-fns/locale"
import { getDateLocale } from "@/utils/dateUtils";
import { Calendar as CalendarIcon, Clock } from "lucide-react"
import { cn, formatDateToLocal, parseDateFromLocal } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export function DatePickerTR({ 
  date,
  value, // alias for date
  onSelect,
  onChange, // alias for onSelect
  placeholder = "Tarih seçin",
  className,
  disabled = false,
  minDate,
  maxDate,
  fromYear = 1950,
  toYear = new Date().getFullYear() + 5,
  returnDateString = false, // if true, returns YYYY-MM-DD string instead of Date object
  ...rest // forward extra props (e.g. data-testid) to the trigger button
}) {
  const [open, setOpen] = React.useState(false);
  
  // Support both 'date' and 'value' props
  // Also handle string dates (YYYY-MM-DD format)
  const rawDate = date || value;
  const selectedDate = React.useMemo(() => {
    if (!rawDate) return null;
    if (rawDate instanceof Date) return rawDate;
    if (typeof rawDate === 'string') {
      // Parse YYYY-MM-DD string to local Date
      return parseDateFromLocal(rawDate);
    }
    return null;
  }, [rawDate]);
  
  // Support both 'onSelect' and 'onChange' props
  const handleChange = onSelect || onChange;
  
  const handleSelect = (selectedDate) => {
    if (handleChange) {
      if (returnDateString && selectedDate) {
        // Return YYYY-MM-DD string in local timezone
        handleChange(formatDateToLocal(selectedDate));
      } else {
        handleChange(selectedDate);
      }
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal rounded-xl",
            !selectedDate && "text-muted-foreground",
            className
          )}
          {...rest}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selectedDate ? format(selectedDate, "d MMMM yyyy", { locale: getDateLocale() }) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          locale={tr}
          fromYear={fromYear}
          toYear={toYear}
          disabled={(date) => {
            if (minDate && date < minDate) return true;
            if (maxDate && date > maxDate) return true;
            return false;
          }}
          initialFocus={false}
        />
      </PopoverContent>
    </Popover>
  )
}

export function DateRangePickerTR({ 
  startDate, 
  endDate, 
  onStartChange, 
  onEndChange,
  className 
}) {
  return (
    <div className={cn("flex gap-2 items-center", className)}>
      <DatePickerTR 
        date={startDate} 
        onSelect={onStartChange} 
        placeholder="Başlangıç"
      />
      <span className="text-grey">-</span>
      <DatePickerTR 
        date={endDate} 
        onSelect={onEndChange} 
        placeholder="Bitiş"
      />
    </div>
  )
}

// Drop-in replacement for <Input type="date">: value "YYYY-MM-DD", onChange receives { target: { value } }
export function DateInputTR({ value, onChange, className, ...rest }) {
  return (
    <DatePickerTR
      value={value || null}
      returnDateString
      fromYear={1930}
      onChange={(v) => onChange?.({ target: { value: v || "" } })}
      className={cn("h-10", className)}
      {...rest}
    />
  );
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

const TimeGrid = ({ items, active, onPick, label, cols, testId, kind }) => (
  <div>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500 mb-1.5">{label}</p>
    <div className={cn("grid gap-1", cols)}>
      {items.map((x) => (
        <button key={x} type="button" onClick={() => onPick(x)}
          className={cn("h-9 rounded-lg text-sm font-semibold tabular-nums transition-colors",
            x === active ? "bg-emerald-600 text-white shadow-sm" : "bg-stone-50 text-[#1F2A44] hover:bg-emerald-50 hover:text-emerald-800")}
          data-testid={testId ? `${testId}-${kind}-${x}` : undefined}>
          {x}
        </button>
      ))}
    </div>
  </div>
);

// Drop-in replacement for <Input type="time">: 24h "HH:MM", 5-minute steps
export function TimeInputTR({ value, onChange, className, disabled, "data-testid": testId }) {
  const [open, setOpen] = React.useState(false);
  const [h, m] = (value || "").split(":");
  const emit = (hh, mm) => onChange?.({ target: { value: `${hh}:${mm}` } });
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={disabled}
          className={cn("w-full h-10 justify-start text-left font-normal rounded-xl tabular-nums", !value && "text-muted-foreground", className)}
          data-testid={testId}>
          <Clock className="mr-2 h-4 w-4" />
          {value || "--:--"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] max-w-[calc(100vw-24px)] p-3 rounded-2xl" align="start" data-testid={testId ? `${testId}-popover` : undefined}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-2xl font-bold text-[#1F2A44] tabular-nums" style={{ fontFamily: "Manrope" }}>{h || "--"}:{m || "--"}</p>
          <button type="button" onClick={() => setOpen(false)}
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors"
            data-testid={testId ? `${testId}-done` : undefined}>Tamam</button>
        </div>
        <div className="space-y-3">
          <TimeGrid label="Saat" kind="h" cols="grid-cols-6" items={HOURS} active={h} testId={testId} onPick={(x) => emit(x, m || "00")} />
          <TimeGrid label="Dakika" kind="m" cols="grid-cols-6" items={MINUTES} active={m} testId={testId} onPick={(x) => { emit(h || "09", x); setOpen(false); }} />
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DatePickerTR;
